import { useState } from "react";
import { Box, Button, Grid2 as Grid, Link as MuiLink, Stack, Typography, alpha, type Theme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { SectionCard } from "@/components/common/SectionCard";
import { TabContent } from "@/components/common/PageLayout";
import { LaseredDeductibleChip } from "@/components/ui/mui/LaseredDeductibleChip";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ResourceName } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatDateRange, formatLongDate, DATE_FORMATS } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { policyDisplayLabel, useStoplossClaimContext } from "../../hooks/useStoplossClaimContext";
import { deductibleWord } from "../rail/claimAmountCopy";
import { RepairClaimLinksDialog } from "./RepairClaimLinksDialog";

const quietLink = {
  color: "inherit",
  textDecoration: "underline",
  // From the palette, so the underline stays faint in dark mode too (#7581 AC8).
  textDecorationColor: (t: Theme) => alpha(t.palette.text.primary, 0.25),
  "&:hover": { color: "primary.main", textDecorationColor: "currentColor" },
} as const;

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Grid size={{ xs: 6, md: 4 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} component="div">
      {children}
    </Typography>
  </Grid>
);

/**
 * One receipt line. `kind`:
 * - "line": a figure that feeds the next subtotal (a leading "−" marks a deduction);
 * - "subtotal": the result of the lines above it, ruled off with a single line;
 * - "total": the figure the receipt is for, ruled off with a heavy line;
 * - "gap": vertical space between two groups.
 */
const Row = ({
  label,
  value,
  kind = "line",
  deduct = false,
  muted = false,
}: {
  label: React.ReactNode;
  value: string;
  kind?: "line" | "subtotal" | "total" | "gap";
  deduct?: boolean;
  muted?: boolean;
}) => {
  if (kind === "gap") return <Box sx={{ height: 12 }} />;
  const heavy = kind === "total";
  const ruled = kind === "subtotal" || heavy;
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 2,
        py: 0.75,
        borderTop: ruled ? (heavy ? "2px solid" : "1px solid") : 0,
        borderColor: heavy ? "text.primary" : "divider",
        fontSize: heavy ? "0.95rem" : "0.8125rem",
        color: muted ? "text.secondary" : "text.primary",
        fontWeight: heavy ? 700 : 400,
      }}
    >
      <Box component="span" sx={{ minWidth: 0 }}>
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontWeight: heavy ? 700 : kind === "subtotal" ? 600 : 500,
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {deduct ? `− ${value}` : value}
      </Box>
    </Box>
  );
};

const money = (n?: number) => (typeof n === "number" ? formatUSD(n) : "—");

/**
 * Details tab (#7586): read-only facts with links on the left, the claim-amount
 * arithmetic on the right. Amounts and dates are engine-written and are shown,
 * not edited; the links are repaired through an explicit action; notes live in
 * the rail.
 *
 * The subtraction "over the deductible − already on other claims = claim amount"
 * is rendered only when the engine's rule actually produced this amount
 * (`reconciles` in the context). Frozen claims keep the amount they were filed
 * with, ASD claims lose part of the overage to the corridor, family claims are
 * sized on the pool, contract-laser claims use a windowed spend. Those show
 * today's facts instead.
 */
export const StoplossClaimDetailsTab = () => {
  const ctx = useStoplossClaimContext();
  const {
    claim,
    displayClaimantId,
    claimant,
    client,
    policy,
    vendor,
    effectiveDeductible,
    isLasered,
    isASD,
    isFamily,
    isFrozen,
    reconciles,
    claimantTotalPaid,
    spendOverDeductible,
    reimbursedTotal,
    priorLive,
    priorStored,
    otherClaimsCount,
  } = ctx;
  const { canEdit } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const [repairOpen, setRepairOpen] = useState(false);

  const claimAmount = claim.total_paid_amount ?? 0;
  const outstanding = Math.max(claimAmount - reimbursedTotal, 0);
  const source = effectiveDeductible?.source;
  const word = deductibleWord(source);
  const claimsTabHref = displayClaimantId ? `/claimants/${displayClaimantId}/show/claims` : undefined;

  const link = (to: string, text: string) => (
    <MuiLink component={RouterLink} to={to} sx={quietLink}>
      {text}
    </MuiLink>
  );
  const othersLabel = (n: number | undefined) =>
    n && claimsTabHref ? (
      <>Already on {link(claimsTabHref, `${n} other claim${n === 1 ? "" : "s"}`)} on this policy</>
    ) : (
      "Already on other claims on this policy"
    );

  const explanation = reconciles
    ? "Read top to bottom: spend less the deductible is the overage; less what is already on other claims is this claim; less what the carrier has paid is what is outstanding."
    : isFrozen
      ? "This claim is submitted or funded, so the engine no longer rewrites its amount; later spend files on a new claim."
      : isASD
        ? "Aggregate-specific policy: part of the overage is absorbed by the ASD corridor before a claim is sized."
        : isFamily
          ? "Family deductible: the claim is sized on the family pool, not this claimant alone."
          : source === "CONTRACT"
            ? "Contract laser: the claim is sized on spend inside the contract window, which is not shown here."
            : "The amount was set by the engine at load; the figures above are the claimant's position today.";

  return (
    <TabContent>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard
            compact
            title="Claim details"
            action={
              canEdit ? (
                <Button size="small" onClick={() => setRepairOpen(true)} sx={{ textTransform: "none" }}>
                  Repair links…
                </Button>
              ) : undefined
            }
          >
            <Grid container spacing={1.5}>
              <Fact label="Claimant">
                {displayClaimantId && claimant
                  ? link(`/claimants/${displayClaimantId}/show`, claimant.claimant_name ?? "Claimant")
                  : "—"}
              </Fact>
              <Fact label="Client">{client ? link(`/clients/${client.id}/show`, client.client_legal_name) : "—"}</Fact>
              <Fact label="Policy">
                {policy ? (
                  link(`/policies/${policy.id}/show`, policyDisplayLabel(policy))
                ) : (
                  <Typography component="span" color="error.main" variant="inherit">
                    Not linked
                  </Typography>
                )}
              </Fact>
              <Fact label="Claim type">
                {claim.stoploss_claim_type ? formatToTitleCase(claim.stoploss_claim_type) : "—"}
              </Fact>
              <Fact label="Administering TPA">{vendor?.company_name ?? "—"}</Fact>
              <Fact label="Source system id">{claim.source_system_id || "—"}</Fact>
              <Fact label="Service period">
                {formatDateRange(claim.first_service_date, claim.last_service_date, DATE_FORMATS.DISPLAY_SHORT) || "—"}
              </Fact>
              <Fact label="Paid period">
                {formatDateRange(claim.first_paid_date, claim.last_paid_date, DATE_FORMATS.DISPLAY_SHORT) || "—"}
              </Fact>
              <Fact label="Submitted date">{claim.submitted_date ? formatLongDate(claim.submitted_date) : "—"}</Fact>
              <Fact label="Created">{claim.created_datetime ? formatLongDate(claim.created_datetime) : "—"}</Fact>
              <Fact label="Last changed">
                {claim.modified_datetime ? formatLongDate(claim.modified_datetime) : "—"}
              </Fact>
              <Fact label="Claim id">
                <Typography variant="body2" color="text.secondary" component="span" sx={{ wordBreak: "break-all" }}>
                  {claim.aegle_id}
                </Typography>
              </Fact>
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Estimated future expenses {money(claim.estimated_future_expenses)}. Accel funded{" "}
              {money(claim.total_amount_accel_funded)}. Billed {money(claim.total_billed_amount)}. Allowed{" "}
              {money(claim.total_allowed_amount)}.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <SectionCard compact title="How the claim amount is built">
            <Stack data-testid="claim-ledger" data-reconciles={reconciles ? "true" : "false"}>
              {reconciles ? (
                <>
                  {/* Top to bottom: spend, less the deductible, is the overage; less what is already on
                      other claims is this claim; less what the carrier has paid is what is outstanding. */}
                  <Row label="Claimant spend on policy" value={money(claimantTotalPaid)} />
                  <Row
                    label={
                      <>
                        {formatToTitleCase(word) || "Specific"} deductible
                        {isLasered ? (
                          <>
                            {" "}
                            <LaseredDeductibleChip />
                          </>
                        ) : null}
                      </>
                    }
                    value={money(effectiveDeductible?.amount)}
                    deduct
                  />
                  <Row label="Over the deductible" value={formatUSD(spendOverDeductible as number)} kind="subtotal" />
                  {(otherClaimsCount ?? 0) > 0 || (priorLive ?? 0) > 0 ? (
                    <Row label={othersLabel(otherClaimsCount)} value={formatUSD(priorLive as number)} deduct />
                  ) : null}
                  <Row label="Claim amount" value={formatUSD(claimAmount)} kind="total" />
                </>
              ) : (
                <>
                  {!isASD && !isFamily && source !== "EXCLUDED" ? (
                    <>
                      <Row label="Claimant spend on policy today" value={money(claimantTotalPaid)} />
                      <Row
                        label={
                          <>
                            {formatToTitleCase(word) || "Specific"} deductible
                            {isLasered ? (
                              <>
                                {" "}
                                <LaseredDeductibleChip />
                              </>
                            ) : null}
                          </>
                        }
                        value={money(effectiveDeductible?.amount)}
                        deduct
                      />
                      <Row label="Over the deductible today" value={money(spendOverDeductible)} kind="subtotal" />
                      <Row kind="gap" label="" value="" />
                    </>
                  ) : null}
                  <Row
                    label={
                      isFrozen
                        ? "On other claims, as recorded when this claim was created"
                        : "Netted against other claims"
                    }
                    value={formatUSD(isFrozen ? priorStored : (priorLive ?? priorStored))}
                    muted
                  />
                  <Row label="Claim amount, as the engine sized it" value={formatUSD(claimAmount)} kind="total" />
                </>
              )}
              <Row label="Reimbursed" value={formatUSD(reimbursedTotal)} deduct />
              <Row label="Outstanding" value={formatUSD(outstanding)} kind="subtotal" />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1.5 }}
              data-testid="ledger-note"
            >
              {explanation}
              {isFamily && displayClaimantId ? (
                <> {link(`/claimants/${displayClaimantId}/show/deductibles`, "View the family deductible")}</>
              ) : null}
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>
      {canEdit && <RepairClaimLinksDialog open={repairOpen} onClose={() => setRepairOpen(false)} claim={claim} />}
    </TabContent>
  );
};
